import { useCallback, useEffect, useState } from "react"
import { Link, router } from "expo-router"
import { View } from "react-native"
import {
  useCheckAccountExists,
  useLoginMutation,
  useRegistrationMutation,
} from "@/queries/user"
import { PrimaryDarkButton } from "@/components/buttons/CommonButtons"
import { showErrorToast } from "@/components/toasts/AppToast"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import Head from "expo-router/head"
import BFText, { H1 } from "@/components/standard/BFText"
import { cn } from "@/components/util/cn"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import type { UsernamePasswordLoginMutation } from "@/generated/graphql"
import { OrDivider } from "@/screens/onboarding/components/OrDivider"
import { SocialLogin } from "@/screens/onboarding/components/SocialLogin"
import useBrowserInfo from "@/hooks/useBrowserInfo"
import { EmailAndPasswordForm } from "@/screens/onboarding/components/EmailAndPasswordForm"
import * as signInEvents from "@/analytics/events/sign-in"
import { A } from "@/components/standard/Html"
import { Vertical } from "@/components/standard/BFLayout"

export default function LoginScreen() {
  const registrationMutation = useRegistrationMutation()
  const loginMutation = useLoginMutation()
  const checkAccountExists = useCheckAccountExists()

  const { isWeb } = useDeviceAttributes()

  const { isEmbeddedBrowser } = useBrowserInfo()

  const hideSocialLogin = isWeb && isEmbeddedBrowser

  useViewedCallback(() => {
    setStatusBarStyle("light")
    signInEvents.signInViewed()
  })

  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const hasPendingMutations =
    registrationMutation.isPending || checkAccountExists.isPending

  const validate = useCallback((): boolean => {
    if (email?.length === 0) {
      setEmailError("Invalid email address")
      return false
    }
    if ((password?.length || 0) < 7) {
      setPasswordError("Invalid password. Minimum of 7 characters.")
      return false
    }
    return true
  }, [email, setEmailError, password, setPasswordError])

  useEffect(() => {
    if (emailError) {
      setEmailError(null)
    }
    if (passwordError) {
      setPasswordError(null)
    }
  }, [email, password])

  const onLogin = () => {
    if (validate()) {
      signInEvents.emailSignInSubmitted({ email })
      loginMutation.mutate(
        {
          username: email,
          password,
        },
        {
          onSuccess: (data: UsernamePasswordLoginMutation) => {
            if (data?.login?.token) {
              // _layout.tsx takes care of redirecting
              console.log("Login successful")
            } else {
              showErrorToast({
                title: "Incorrect Password",
                message: "Click here if you forgot your password.",
                onPress: () => {
                  router.push("/reset-password")
                },
              })
            }
          },
          onError: (error) => {
            const errorResponse = (error as any)?.response
            if (errorResponse.errors) {
              showErrorToast({
                title: "Sign In Error",
                message: errorResponse.errors[0].message,
              })
            }
          },
        }
      )
    }
  }

  return (
    <>
      <Head>
        <title>Backflip - Login</title>
      </Head>
      <Vertical className={"justify-between flex-1"}>
        <View
          className={cn("flex flex-col flex-grow", isWeb && "flex-grow-0 ")}
        >
          <View className={"py-7 gap-5"}>
            <H1 color={"white"}>Hi! Welcome back.</H1>
          </View>

          {!hideSocialLogin && (
            <>
              <SocialLogin loginType="login" />
              <OrDivider hide={hideSocialLogin} />
            </>
          )}

          <EmailAndPasswordForm
            email={email}
            setEmail={(v) => {
              setEmail(v || "")
            }}
            emailError={emailError}
            password={password}
            setPassword={(p) => {
              setPassword(p || "")
            }}
            passwordError={passwordError}
          />

          <View className="flex flex-col items-center justify-start flex-1">
            <Link
              allowFontScaling={false}
              href={"/reset-password"}
              className="text-[12px] font-spacegrotesk text-teal underline leading-4"
            >
              Forgot Password?
            </Link>
            <BFText className="text-white text-[12px] font-spacegrotesk text-center mt-[13px] mb-3">
              Don&apos;t have an account?&nbsp;
              <BFText
                className="underline text-teal font-spacegrotesk"
                onPress={() => {
                  if (router.canGoBack()) {
                    router.back()
                  } else {
                    router.push("/register")
                  }
                }}
              >
                Sign Up
              </BFText>
            </BFText>
          </View>
        </View>

        <View className="flex flex-col justify-start w-full shrink-0">
          <PrimaryDarkButton
            testID="sign-in-button"
            title={"Sign In"}
            onPress={onLogin}
            disabled={hasPendingMutations}
          />
          <BFText className="text-white text-[10px] font-spacegrotesk text-center m-[6px]">
            <A
              className="underline text-teal font-spacegrotesk"
              href="https://dobackflip.com/backflip-terms-of-use-agreement/"
            >
              Terms of Use
            </A>
            &nbsp;and&nbsp;
            <A
              className="underline text-teal font-spacegrotesk"
              href="https://dobackflip.com/privacy/"
            >
              Privacy Policy
            </A>
          </BFText>
        </View>
      </Vertical>
    </>
  )
}
